import { Component } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const RELOAD_KEY = "bbk_crash_reload";
const isChunkError = (m = "") => /ChunkLoadError|Loading (CSS )?chunk|Unexpected token '<'|dynamically imported module|Importing a module script failed/i.test(m);

export const reportClientError = (message, stack = "") => {
  try {
    fetch(`${API}/api/client-errors`, {
      method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
      body: JSON.stringify({ message: String(message).slice(0, 500), stack: String(stack).slice(0, 2000), path: window.location.pathname, ua: navigator.userAgent }),
    }).catch(() => {});
  } catch { /* never let reporting crash the page */ }
};

// Stale/failed script download → reload once to fetch the fresh version.
const reloadOnce = () => {
  if (sessionStorage.getItem(RELOAD_KEY)) return false;
  sessionStorage.setItem(RELOAD_KEY, "1");
  window.location.reload();
  return true;
};

export const installCrashGuards = () => {
  window.addEventListener("error", (e) => {
    const msg = e?.message || "";
    if (isChunkError(msg) && reloadOnce()) return;
    if (msg && !/ResizeObserver|Script error|zoid destroyed/i.test(msg)) reportClientError(msg, e?.error?.stack);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = e?.reason?.message || String(e?.reason || "");
    if (isChunkError(msg)) reloadOnce();
  });
  window.addEventListener("load", () => setTimeout(() => sessionStorage.removeItem(RELOAD_KEY), 10000));
};

export class AppErrorBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() { return { failed: true }; }

  componentDidCatch(error, info) {
    if (isChunkError(error?.message) && reloadOnce()) return;
    reportClientError(error?.message || "render error", `${error?.stack || ""}\n${info?.componentStack || ""}`);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div data-testid="app-crash-screen" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, background: "#0A0C10", color: "#e2e8f0", fontFamily: "Arial, sans-serif", padding: 24, textAlign: "center" }}>
        <img src="/bb-logo.png" alt="B&B KIGALI FM" width={80} height={80} style={{ borderRadius: 16, background: "#fff" }} />
        <div style={{ fontSize: 18, fontWeight: 800 }}>Something went wrong</div>
        <div style={{ fontSize: 13, color: "#94a3b8", maxWidth: 320 }}>Please reload B&B KIGALI FM. Our team has been notified automatically.</div>
        <button data-testid="app-crash-reload-btn" onClick={() => { sessionStorage.removeItem(RELOAD_KEY); window.location.reload(); }}
          style={{ height: 44, padding: "0 28px", borderRadius: 999, background: "#E11D2A", color: "#fff", fontWeight: 700, border: 0, cursor: "pointer" }}>Reload</button>
      </div>
    );
  }
}

// Isolates one part of the app: if it fails, only that part is hidden/replaced — the rest (and the radio) keeps working.
export class SafeZone extends Component {
  state = { failed: false, key: this.props.resetKey };

  static getDerivedStateFromError() { return { failed: true }; }

  static getDerivedStateFromProps(p, s) { return p.resetKey !== s.key ? { failed: false, key: p.resetKey } : null; }

  componentDidCatch(error, info) {
    reportClientError(`[${this.props.name || "zone"}] ${error?.message || "render error"}`, `${error?.stack || ""}\n${info?.componentStack || ""}`);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return this.props.fallback ?? null;
  }
}

export const PageFallback = () => (
  <div data-testid="page-error-fallback" className="max-w-md mx-auto my-20 px-6 text-center">
    <div className="text-white font-bold text-lg">This page couldn't load</div>
    <p className="text-slate-400 text-sm mt-1">The rest of B&amp;B KIGALI FM — including the live radio — keeps working.</p>
    <button onClick={() => window.location.reload()} data-testid="page-error-retry"
      className="mt-4 h-11 px-6 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold transition-colors">Try again</button>
  </div>
);
